<template>
  <div class="button-container">
    <!-- 显示前4个按钮 -->
    <el-button 
      size="small" 
      class="x-buttons" 
      v-for="(obj, i) in visibleButtons" 
      :key="i" 
      :color="obj.color"
      v-show="obj.show?obj.show():true"
      :loading="obj.loading" 
      @click="onClickFn(obj)"
    >{{ obj.label }}</el-button>
    
    <!-- 更多按钮 dropdown -->
    <el-dropdown v-if="moreButtons.length > 0" trigger="click">
      <el-button size="small" class="x-buttons more-button" :color="'#909399'">
        更多 <el-icon><ArrowDown /></el-icon>
      </el-button>
      <template #dropdown>
        <div class="more-buttons-container">
          <el-button 
            size="small" 
            class="x-buttons more-button" 
            v-for="(obj, i) in moreButtons" 
            :key="i" 
            :color="obj.color"
            v-show="obj.show?obj.show():true"
            :loading="obj.loading" 
            @click="onClickFn(obj)"
          >{{ obj.label }}</el-button>
        </div>
      </template>
    </el-dropdown>
    
    <!-- 导出选择弹窗 -->
    <ComDialog
      :isShow="exportDialogVisible"
      :width="450"
      title="导出选择"
      alignCenter
      @close="cancelExport"
    >
      <div class="export-dialog-content">
        <span class="export-tip">请选择导出方式：</span>
        <div class="export-options">
          <el-radio-group v-model="exportType" class="export-radio-group">
            <el-radio value="all" class="export-radio">全量导出</el-radio>
            <el-radio value="filter" class="export-radio">按筛选条件导出</el-radio>
            <el-radio value="check" class="export-radio">按选中项导出</el-radio>
          </el-radio-group>
        </div>
      </div>
      <div class="tips-footer">
        <div class="footer-btn">
          <el-button  @click="cancelExport">取消</el-button>
          <el-button type="primary" style="margin-left:10px;" @click="confirmExport">确认</el-button>
        </div>
      </div>
    </ComDialog>
  </div>
</template>
<script lang="ts" setup>
import { downloadFileFn } from "@/utils/index.ts";
import { defineEmits, defineProps, ref, watch, computed } from "vue";
const emits = defineEmits(["update:multipleSelection", "rowClick"]);
import comCueBox from "../comCue/comCue";
import { ElMessage } from "element-plus";
import { ArrowDown } from "@element-plus/icons-vue";

const props = defineProps({
  list: Array,
  checkedList: Array,
  filterParams: Object,
  maxVisible: Number
});

const defaultColor: Record<string, any> = {
  add: "#409eff",
  export: "#67c23a",
  import: "#e6a23c",
  delete: "#f14848"
};

const sortMap: Record<string, any> = {
  add: 40,
  export: 30,
  import: 20,
  delete: 10
};

const bList = ref<Array<Record<string, any>>>([]);
// 导出弹窗相关状态
const exportDialogVisible = ref(false);
const exportType = ref('all'); // 默认按筛选条件导出
const currentExportObj = ref<any>(null);

// 计算属性：显示的前4个按钮
const visibleButtons = computed(() => bList.value.slice(0, props.maxVisible||5));
// 计算属性：更多按钮（第5个及以后）
const moreButtons = computed(() => bList.value.slice(props.maxVisible||5));

// 使用watch响应props.list的变化，确保bList总是正确初始化
watch(() => props.list, (newList) => {
  bList.value = [];
  (newList || []).forEach((o: any) => {
    const obj = {
      ...o,
      loading: false
    };
    if (obj.type in defaultColor) {
      obj["color"] = defaultColor[obj.type];
      bList.value.push(obj);
    } else if (obj.type === "custom") {
      obj["color"] = generateSoftColor();
      bList.value.push(obj);
    }
  });
  bList.value.sort((a, b) => (sortMap[b.type] || 0) - (sortMap[a.type] || 0));
}, { immediate: true }); // 立即执行一次

const tipsPro = (s) => {
  return new Promise(r => {
    comCueBox(`请确认执行【${s}】操作？`).then(() => {
      r(true)
    })
  })
}

// 确认导出
const confirmExport = async () => {
  if (currentExportObj.value && currentExportObj.value.url) {
    const url = currentExportObj.value.url;
    // 获取参数，传入导出类型判断导出类型，再确定是否提交参数
    let params: Record<string, any> = {};
     
    // 导出类型判断是否需要提交参数
    if (exportType.value === 'filter') {
        params =currentExportObj.value.params?currentExportObj.value.params() : {...props.filterParams};
        delete params.pageDomain;
    }

    if(exportType.value === 'check'&&props.checkedList.length == 0){
      ElMessage.error('请选择要导出的数据项');
      return
    }

    if(exportType.value === 'check'&&props.checkedList.length){
     // 判断路由是vipClientMgt/vipClientIncomePage，需要提交orderId【特性场景】
      params = {
        idList: props.checkedList.map((o: any) =>{ 
          return currentExportObj.value.url.indexOf('/keyAccountUser/keyCustomer/export')>-1?o.orderId:o.id;})
      }
    }
    
    currentExportObj.value.loading = true;
    try {
      await downloadFileFn({url, params});
      ElMessage.success('导出成功');
    } catch (error) {
      ElMessage.error('导出失败');
    } finally {
      currentExportObj.value.loading = false;
      exportDialogVisible.value = false;
    }
  }
};

// 取消导出
const cancelExport = () => {
  exportDialogVisible.value = false;
  currentExportObj.value = null;
};

const onClickFn = async (obj: any) => {
  let flag = true
  // 如果有其他按钮的loading为true的时候，则不能点击，提示：有操作任务执行中，请稍后再试
  if (bList.value.some((o: any) => o.loading)) {
    ElMessage.error('有操作任务执行中，请稍后再试');
    flag = false;
    return
  }
  if (obj.type === 'export') {
    if (obj.url) {
      // 显示导出选择弹窗
      currentExportObj.value = obj;
      exportType.value = 'filter'; // 重置为默认值
      exportDialogVisible.value = true;
    }
  } else {
    if (obj.type == 'delete') {
      flag = await tipsPro(obj.label) as boolean
    }
    flag && obj.click((s: any) => {
      if (typeof s === "boolean") {
        obj.loading = s;
      } else {
        ElMessage.success(`${obj.label}成功！`);
        obj.loading = false;
      }
    });
  }
};

function generateSoftColor() {
  const getSoftValue = () => {
    // 限制在 80-180 之间，避免太亮（否则白色文字看不清）
    return Math.floor(Math.random() * 100) + 80; // 80-179
  };

  // 生成RGB分量
  let r = getSoftValue();
  let g = getSoftValue();
  let b = getSoftValue();

  // 随机增强1-2个颜色分量（避免颜色太灰）
  const boostComponent = (value: any) => Math.min(value + 30, 180); // 增强但不超过180
  const components = ["r", "g", "b"];
  const boostCount = Math.random() > 0.5 ? 1 : 2; // 随机增强1或2个分量
  const boostedComponents = components
    .sort(() => 0.5 - Math.random())
    .slice(0, boostCount);

  if (boostedComponents.includes("r")) r = boostComponent(r);
  if (boostedComponents.includes("g")) g = boostComponent(g);
  if (boostedComponents.includes("b")) b = boostComponent(b);

  // 转换为十六进制
  const toHex = (c: any) => c.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
</script>
<style lang='scss' scoped>
.button-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.x-buttons.el-button {
  margin-bottom: 5px; 
  color: white !important;
  span {
    color: white !important;
  }
}

/* 更多按钮下拉菜单样式 */
.more-buttons-container {
  padding: 15px;
  width: 200px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  box-sizing: border-box;
}
.el-button+.el-button {
  margin-left: 0;
}

.more-button {
  flex: 0 0 calc(50% - 20px); /* 2个按钮 per 行，减去间隙 */
  margin-bottom: 0;
}

/* 导出弹窗样式 */

.export-tip {
  font-size: 14px;
  color: #303133;
  display: block;
  margin-bottom: 20px;
}

.export-radio-group {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.export-radio {
  font-size: 14px;
  color: #606266;
}

.tips-footer {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  .footer-btn {
    display: flex;
    align-items: center;
    .el-button {
      padding: 12px 20px;
      &:first-child {
        color: #303133!important;
      }
    }
  }
}
</style>